import React, { useState, useEffect, useContext, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { isEmpty } from "lodash";

// Routes
import paths from "routes/paths";

// Utils
import { checkEmailInput, checkIsRequiredFieldValue, checkStringIsLongerThan } from "utils/validations";
import { updatePersonalization } from "utils/module/store/personalization";
import { signupShop } from "utils/actions";
import { getUserLanguageBasedOnUrlParams } from "utils/i18n/languageUtils";

// Context
import { UserContext } from "contexts/UserContext";

// Components
import { Flex } from "components/spocketUI";

// API
import { signUp } from "utils/api/user/requests";

// Constants
import { APP_ASSETS_BUCKET_URL } from "constants/common";

// Style
import {
  Container,
  Form,
  FormContainer,
  FormFieldContainer,
  StyledInput,
  EmailErrorText,
  NameErrorText,
  GenericErrorText,
  StyledButton,
  ErrorIconContainer,
  Icon
} from "./SignUpForm.style";
import { AlreadyHaveAccountText, StyledLink } from "pages/Login/style";

// Assets
const ErrorIcon = `${APP_ASSETS_BUCKET_URL}/icon-error.svg`;

const getUserLanguage = () => {
  const userLanguage = getUserLanguageBasedOnUrlParams();

  return !isEmpty(userLanguage) ? userLanguage : "en";
};

const SignUpFormComponent = ({
  setIsLoading,
  queryEmail,
  signupShop,
  isFormActive,
  isPhone,
  onFocusForm
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setStoreInfo } = useContext(UserContext);
  const [userName, setUserName] = useState("");
  const [userNameError, setUserNameError] = useState("");
  const [validatedToken, setValidatedToken] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [signupError, setSignupError] = useState("");
  const hCaptchaKey = process.env.REACT_APP_HCAPTCHA_SITE_KEY;
  const captchaRef = useRef(null);
  const MIN_NAME_LENGTH = 2;
  const [captchaToken, setCaptchaToken] = useState(null);
  const [searchParams] = useSearchParams();

  const shouldDisableCaptcha = searchParams.get("qa") === "yes";

  useEffect(() => {
    if (queryEmail) {
      if (email && userName) {
        // Timeout here to be sure captcha js is loaded
        setTimeout(() => {
          checkCaptcha();
        }, 1000);
      } else {
        setUserName(queryEmail.split("@")[0]);
        setEmail(queryEmail);
      }
    }
  }, [queryEmail, email, userName]);

  const validate = () => {
    const emailError = checkEmailInput(email);
    setEmailError(emailError);
    const nameRequiredValidation = checkIsRequiredFieldValue(userName);
    const nameLengthValidation = checkStringIsLongerThan({ string: userName, val: MIN_NAME_LENGTH });
    if (!nameRequiredValidation) {
      setUserNameError(t("Signup.SignUpForm.Name.Error"));
    } else if (!nameLengthValidation) {
      setUserNameError(t("Signup.SignUpForm.Error.Name"));
    } else {
      setUserNameError("");
    }
    const nameValidationPassed = nameRequiredValidation && nameLengthValidation;
    return emailError.length > 0 || !nameValidationPassed;
  };

  const onCaptchaError = () => {
    setSignupError(t("Checkout.Error.Captcha"));
  };

  useEffect(() => {
    if (captchaToken) {
      handleSubmit();
    }
  }, [captchaToken]);

  const checkCaptcha = async event => {
    if (event) event.preventDefault();

    if (shouldDisableCaptcha) return handleSubmit();

    try {
      const captchaResponse = await captchaRef.current.execute({
        async: true
      });
      const responseToken = captchaResponse.response;
      setCaptchaToken(responseToken);
    } catch {
      onCaptchaError();
    }
  };

  const handleSubmit = () => {
    const captchaIsValidated = captchaToken || validatedToken || shouldDisableCaptcha;
    const hCaptchaToken = captchaToken || validatedToken;

    if (captchaIsValidated) {
      const hasErrors = validate();
      if (!hasErrors) {
        setIsLoading(true);
        signUp(
          {
            email,
            userName,
            hCaptchaToken
          },
          shouldDisableCaptcha ? { headers: { platform: "qa_automation" } } : undefined
        )
          .then(response => {
            const { data } = response;
            const userLanguage = !isEmpty(localStorage.getItem("language"))
              ? localStorage.getItem("language")
              : getUserLanguage();

            signupShop({ action: "signed_up", ...data }, false, navigate);

            if (!isEmpty(userLanguage)) {
              updatePersonalization({ language: userLanguage });
            }
          })
          .catch(err => {
            const { response } = err;
            const { data } = response;
            const { error } = data;
            if (error && error.email) {
              if (queryEmail) {
                setStoreInfo("emailUsedOnSignup", email);
                navigate(paths.auth.login);
              } else {
                setEmailError(t("Signup.SignUpForm.Error.Email"));
              }
              setIsLoading(false);
            } else {
              setIsLoading(false);
              setSignupError(t("Signup.SignUpForm.Error.Generic"));
            }
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        setIsLoading(false);
      }
    } else {
      onCaptchaError();
    }
  };

  return (
    <Container id="signup-form">
      <Form onSubmit={checkCaptcha}>
        <FormContainer>
          <FormFieldContainer>
            <StyledInput
              name="email"
              type="text"
              hasError={emailError}
              placeholder={t("Signup.SignUpForm.Email")}
              value={email}
              onClick={isPhone ? () => onFocusForm() : null}
              onChange={val => {
                setEmail(val.target.value);
                setEmailError("");
                setSignupError("");
              }}
              data-cy="sign-up-input-email"
            />
            {emailError ? (
              <>
                <ErrorIconContainer type="email">
                  <Icon src={ErrorIcon} alt="error" />{" "}
                </ErrorIconContainer>
                <EmailErrorText>{emailError}</EmailErrorText>
              </>
            ) : null}
          </FormFieldContainer>
          {!isPhone || (isPhone && isFormActive) ? (
            <>
              <FormFieldContainer>
                <StyledInput
                  name="name"
                  type="text"
                  hasError={userNameError}
                  placeholder={t("Signup.SignUpForm.Name")}
                  value={userName}
                  onChange={val => {
                    setUserName(val.target.value);
                    setUserNameError("");
                    setSignupError("");
                  }}
                  data-cy="sign-up-input-name"
                />
                {userNameError ? (
                  <>
                    <ErrorIconContainer type="name">
                      <Icon src={ErrorIcon} alt="error" />{" "}
                    </ErrorIconContainer>
                    <NameErrorText>{userNameError}</NameErrorText>
                  </>
                ) : null}
                {signupError ? <GenericErrorText>{signupError}</GenericErrorText> : null}
              </FormFieldContainer>

              <StyledButton title={t("Signup.SignUpForm.Button.Title")} data-cy="sign-up-button">
                {t("Signup.SignUpForm.Button.Label")}
              </StyledButton>

              <AlreadyHaveAccountText>
                <Trans
                  i18nKey="Signup.SignUpForm.IntroText"
                  components={{
                    1: <StyledLink to={paths.auth.login} />
                  }}
                />
              </AlreadyHaveAccountText>
            </>
          ) : null}

          <Flex marginTop="15px" marginBottom="15px">
            <HCaptcha
              sitekey={hCaptchaKey}
              onVerify={token => setValidatedToken(token)}
              onError={onCaptchaError}
              ref={captchaRef}
              size="invisible"
            />
          </Flex>
        </FormContainer>
      </Form>
    </Container>
  );
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      signupShop
    },
    dispatch
  );
}

export const SignUpForm = connect(null, mapDispatchToProps)(SignUpFormComponent);

SignUpForm.propTypes = {
  setIsLoading: PropTypes.func.isRequired,
  queryEmail: PropTypes.string,
  isFormActive: PropTypes.bool.isRequired,
  isPhone: PropTypes.bool,
  onFocusForm: PropTypes.func.isRequired
};
